import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { format } from 'date-fns';
import { getFontFamily, getPenColor } from '../utils/certificateStyles';
import DoctorSeal from './DoctorSeal';
import TemplateSkin, { getTemplateSkin } from './TemplateSkin';

const RealisticCertificatePreview = ({ type, data, templateType = 'classic', customization = { fontStyle: 'printed', penColor: 'black' } }) => {
  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'd MMMM yyyy');
    } catch {
      return dateString;
    }
  };

  const fontFamily = getFontFamily(customization.fontStyle);
  const penColor = getPenColor(customization.penColor || 'black');
  const skin = getTemplateSkin(templateType);

  const handwrittenStyle = {
    fontFamily: customization.fontStyle !== 'printed' ? fontFamily : 'system-ui',
    color: customization.fontStyle !== 'printed' ? penColor : '#000000',
  };

  const sharedHeaderProps = {
    templateType,
    hospitalName: data.hospitalName,
    doctorName: data.doctorName,
    registrationNumber: data.registrationNumber,
  };

  const SignatureBlock = ({ label = 'Signature & Stamp' }) => (
    <div className="mt-8 pt-4 border-t border-gray-300">
      <div className="flex justify-between items-start gap-4">
        <div className="text-sm">
          <p className="text-gray-600">Issued by:</p>
          <p className="font-semibold text-gray-800" style={{ color: skin.accent }}>
            {data.doctorName || 'Dr. Doctor Name'}
          </p>
          <p className="text-xs text-gray-600">Reg. No.: {data.registrationNumber || 'Registration Number'}</p>
          <p className="text-xs text-gray-600">{data.hospitalName || 'Hospital Name'}</p>
        </div>
        <div className="text-center">
          <DoctorSeal doctorName={data.doctorName} registrationNumber={data.registrationNumber} />
          <p className="text-xs text-gray-400 mt-1">{label}</p>
        </div>
      </div>
    </div>
  );

  const Footer = () => (
    <div className="mt-6 text-center">
      <p className="text-xs text-gray-300">
        Template &middot; Not valid without doctor&apos;s signature &amp; stamp
      </p>
    </div>
  );

  // ---- BODIES (template-agnostic; templates only swap header / accents) ----

  const renderPrescription = () => (
    <TemplateSkin {...sharedHeaderProps}>
      <div className="mb-4">
        <p className="text-3xl font-serif" style={{ color: skin.accent }}>℞</p>
      </div>

      <div className="text-right mb-4">
        <p className="text-sm">Date: <span style={handwrittenStyle}>{formatDate(data.dateOfIssue)}</span></p>
      </div>

      <div className="mb-3" style={handwrittenStyle}>
        <p className="text-lg leading-relaxed">{data.patientName || 'Patient Name'} &middot; Age {data.age || '___'} &middot; {data.gender}</p>
      </div>

      <div className="mb-6" style={handwrittenStyle}>
        <p className="text-lg leading-relaxed">{data.diagnosis === 'Other (Custom)' ? data.customDiagnosis : data.diagnosis}</p>
      </div>

      {data.notes && (
        <div className="mb-6" style={handwrittenStyle}>
          <p className="text-base leading-relaxed">{data.notes}</p>
        </div>
      )}

      {type === 'sick-leave' && (
        <div className="mb-8" style={handwrittenStyle}>
          <p className="text-lg leading-relaxed">
            Rest advised for {data.leaveTo ? Math.ceil((new Date(data.leaveTo) - new Date(data.leaveFrom)) / (1000 * 60 * 60 * 24)) + 1 : '___'} days
          </p>
        </div>
      )}

      <SignatureBlock />
      <Footer />
    </TemplateSkin>
  );

  const renderSickLeave = () => (
    <TemplateSkin {...sharedHeaderProps}>
      <div className="text-center mb-6">
        <h3 className="text-xl font-bold underline" style={{ color: skin.accent }}>MEDICAL CERTIFICATE</h3>
        <p className="text-sm text-gray-600 mt-1">(For Sick Leave)</p>
      </div>

      <div className="text-right mb-4">
        <p className="text-sm">Date: <span style={handwrittenStyle}>{formatDate(data.dateOfIssue)}</span></p>
      </div>

      <div className="mb-6 space-y-3">
        <p className="text-gray-800 leading-relaxed">
          This is to certify that <strong style={handwrittenStyle}>{data.patientName || 'Patient Name'}</strong>,{' '}
          Age: <strong style={handwrittenStyle}>{data.age || '___'}</strong> years,{' '}
          Gender: <strong>{data.gender}</strong>
        </p>
        <p className="text-gray-800 leading-relaxed">has been examined and found to be suffering from:</p>
        <div className="ml-6 my-2" style={handwrittenStyle}>
          <p className="text-lg font-medium">{data.diagnosis === 'Other (Custom)' ? data.customDiagnosis : data.diagnosis}</p>
        </div>
        <p className="text-gray-800 leading-relaxed">
          The patient is advised complete bed rest and medical leave from{' '}
          <strong style={handwrittenStyle}>{formatDate(data.leaveFrom)}</strong> to{' '}
          <strong style={handwrittenStyle}>{formatDate(data.leaveTo)}</strong>.
        </p>
        {data.notes && (
          <div className="mt-4">
            <p className="text-gray-800"><strong>Additional Notes:</strong></p>
            <p className="text-gray-700 ml-4" style={handwrittenStyle}>{data.notes}</p>
          </div>
        )}
      </div>

      <SignatureBlock />
      <Footer />
    </TemplateSkin>
  );

  const renderFitness = () => (
    <TemplateSkin {...sharedHeaderProps}>
      <div className="text-center mb-6">
        <h3 className="text-xl font-bold underline" style={{ color: skin.accent }}>MEDICAL FITNESS CERTIFICATE</h3>
      </div>

      <div className="text-right mb-4">
        <p className="text-sm">Date: <span style={handwrittenStyle}>{formatDate(data.dateOfIssue)}</span></p>
      </div>

      <div className="mb-6 space-y-3">
        <p className="text-gray-800 leading-relaxed">
          This is to certify that I have personally examined <strong style={handwrittenStyle}>{data.patientName || 'Patient Name'}</strong>,{' '}
          Age: <strong style={handwrittenStyle}>{data.age || '___'}</strong> years,{' '}
          Gender: <strong>{data.gender}</strong> and found them to be:
        </p>
        <div className="text-center my-6">
          <p className="text-2xl font-bold" style={{ color: skin.accent }}>MEDICALLY FIT</p>
        </div>
        <p className="text-gray-800 leading-relaxed">
          The candidate is fit for all activities including work, travel, sports and other physical activities.
        </p>
        {data.notes && (
          <div className="mt-4">
            <p className="text-gray-800"><strong>Medical Observations:</strong></p>
            <p className="text-gray-700 ml-4" style={handwrittenStyle}>{data.notes}</p>
          </div>
        )}
      </div>

      <SignatureBlock label="Examined &amp; certified by" />
      <Footer />
    </TemplateSkin>
  );

  const renderGeneral = () => (
    <TemplateSkin {...sharedHeaderProps}>
      <div className="text-center mb-6">
        <h3 className="text-xl font-bold underline" style={{ color: skin.accent }}>GENERAL MEDICAL CERTIFICATE</h3>
      </div>

      <div className="text-right mb-4">
        <p className="text-sm">Date: <span style={handwrittenStyle}>{formatDate(data.dateOfIssue)}</span></p>
      </div>

      <div className="mb-6 space-y-3">
        <p className="text-gray-800 leading-relaxed">
          This is to certify that <strong style={handwrittenStyle}>{data.patientName || 'Patient Name'}</strong>,{' '}
          Age: <strong style={handwrittenStyle}>{data.age || '___'}</strong> years,{' '}
          Gender: <strong>{data.gender}</strong> was examined by me on{' '}
          <strong style={handwrittenStyle}>{formatDate(data.dateOfIssue)}</strong>.
        </p>
        <div className="mt-4">
          <p className="text-gray-800"><strong>Clinical Findings:</strong></p>
          <p className="text-gray-700 ml-4" style={handwrittenStyle}>
            {data.diagnosis === 'Other (Custom)' ? data.customDiagnosis : data.diagnosis}
          </p>
        </div>
        {data.notes && (
          <div className="mt-4">
            <p className="text-gray-800"><strong>Medical Advice:</strong></p>
            <p className="text-gray-700 ml-4" style={handwrittenStyle}>{data.notes}</p>
          </div>
        )}
        <p className="text-gray-800 leading-relaxed mt-4">
          This certificate is issued upon request for general purposes.
        </p>
      </div>

      <SignatureBlock />
      <Footer />
    </TemplateSkin>
  );

  return (
    <Card data-testid="certificate-preview-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>📄 Live Certificate Preview</CardTitle>
          <Badge className="bg-green-500">🟢 Live</Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-auto max-h-[800px]">
          {type === 'prescription' && renderPrescription()}
          {type === 'sick-leave' && renderSickLeave()}
          {type === 'fitness' && renderFitness()}
          {type === 'general' && renderGeneral()}
        </div>
      </CardContent>
    </Card>
  );
};

export default RealisticCertificatePreview;
